import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'
import { WorkspaceBoundary } from './auth/WorkspaceBoundary'
import { developerToolsAvailable } from './intelligence/contextLabAccess'
import { enableWorkspaceBoundary } from './data/workspace'
import { createWorkspaceFetch } from './data/workspaceFetch'
import { readOrCreateBootstrapDeviceId } from './data/db'
import { readNativeStartupLaunchIntent } from './native/nativeInitialLaunch'
import { initializeUiSize } from './utils/uiSize'
import { beaconSender, installClientErrorReporting } from './diagnostics/clientErrors'
import { resolveLinkMetadataApiBaseUrl } from './links/linkMetadata'
import { getSupabaseConfig } from './supabase/config'
import {
  activateWaitingServiceWorker,
  clearAppShellCaches,
  dispatchAppUpdateAvailable,
  queryDeployedBuildId,
  queryWorkerBuildId,
  startServiceWorkerUpdateChecks,
} from './pwaUpdate'
import {
  initializePerformanceDiagnostics,
  recordDiagnosticMilestone,
} from './utils/performanceDiagnostics'
import { getNativePlatform, removeNativeAppShellWorkers } from './utils/nativeRuntime'
import '@minimany/brand/tokens.css'
import './styles.css'
import './design/studio/studio.css'

enableWorkspaceBoundary()
globalThis.fetch = createWorkspaceFetch(globalThis.fetch.bind(globalThis))
document.title = 'miniMany'
// Content-free reports of unhandled errors to the app's own server (src/diagnostics/clientErrors.ts); not in local development.
if (!import.meta.env.DEV) {
  const nativePlatform = getNativePlatform(window)
  const base = resolveLinkMetadataApiBaseUrl({
    configuredBaseUrl: import.meta.env.VITE_API_BASE_URL,
    appEnvironment: getSupabaseConfig()?.environment ?? 'development',
    nativePlatform,
  })
  installClientErrorReporting({
    send: beaconSender(`${base}/api/diagnostics/client-error`),
    build: __MINIMANY_BUILD_ID__,
    surface: nativePlatform === 'android' ? 'android' : 'web',
    online: () => navigator.onLine,
  })
}
// Re-establish the app theme if an older cached document shell omitted it.
document.documentElement.dataset.theme = 'minimany'
document.documentElement.dataset.appBuild = __MINIMANY_BUILD_ID__
performance.mark('minimany:main-evaluated')
initializePerformanceDiagnostics()
recordDiagnosticMilestone('main:evaluated')
const initialUiSize = initializeUiSize({
  nativePlatform: getNativePlatform(window),
  standalone: matchMedia('(display-mode: standalone)').matches,
  storage: localStorage,
  userAgent: navigator.userAgent,
})

const root = document.getElementById('root')!

function mountApplication(): void {
  // Contexts v2 preview (docs/contexts-v2/README.md): a separate read-only page, never the main app
  // shell. A developer tool: never in production builds, and the page checks admin.
  if (/^\/v2(\/|$)/.test(location.pathname) && developerToolsAvailable(import.meta.env.DEV, import.meta.env.VITE_APP_ENV)) {
    void import('./contextsV2/ContextsV2Root').then(({ ContextsV2Root }) =>
      createRoot(root).render(<StrictMode><WorkspaceBoundary><ContextsV2Root/></WorkspaceBoundary></StrictMode>))
    return
  }
  const initialNativeLaunchIntent = readNativeStartupLaunchIntent(window)
  if (initialNativeLaunchIntent) {
    recordDiagnosticMilestone('native-intent:startup-route-seeded', {
      kind: initialNativeLaunchIntent.kind,
    })
  }
  const initialDeviceId = readOrCreateBootstrapDeviceId()
  createRoot(root).render(
    <StrictMode>
      <WorkspaceBoundary><App
        initialDeviceId={initialDeviceId}
        initialNativeLaunchIntent={initialNativeLaunchIntent}
        initialUiSize={initialUiSize}
      /></WorkspaceBoundary>
    </StrictMode>,
  )

}

mountApplication()

let serviceWorkerStarted = false
const startServiceWorker = () => {
  if (serviceWorkerStarted) return
  serviceWorkerStarted = true
  void import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onNeedRefresh: () => undefined,
      onNeedReload: () => undefined,
      onRegisteredSW(serviceWorkerUrl, registration) {
        if (!registration) return
        let announcedBuildId: string | null = null
        let matchingWorkerActivation: Promise<boolean> | null = null
        const activateWorkerAlreadyMatchingPage = () => {
          if (!registration.waiting) return Promise.resolve(false)
          if (matchingWorkerActivation) return matchingWorkerActivation
          matchingWorkerActivation = (async () => {
            const waitingBuildId = await queryWorkerBuildId(registration.waiting)
            if (waitingBuildId !== __MINIMANY_BUILD_ID__) return false
            return activateWaitingServiceWorker(registration, __MINIMANY_BUILD_ID__)
          })().finally(() => {
            matchingWorkerActivation = null
          })
          return matchingWorkerActivation
        }
        const clearStaleCachesForMatchingPage = async () => {
          const controllerBuildId = await queryWorkerBuildId(navigator.serviceWorker.controller)
          if (controllerBuildId === __MINIMANY_BUILD_ID__) {
            await clearAppShellCaches(caches, __MINIMANY_BUILD_ID__)
          }
        }
        const announceAvailableUpdate = async () => {
          const deployedBuildId = await queryDeployedBuildId()
          if (
            deployedBuildId
            && deployedBuildId !== __MINIMANY_BUILD_ID__
            && deployedBuildId !== announcedBuildId
          ) {
            announcedBuildId = deployedBuildId
            dispatchAppUpdateAvailable(deployedBuildId)
          }
          await activateWorkerAlreadyMatchingPage()
          await clearStaleCachesForMatchingPage()
        }
        const lifecycle = startServiceWorkerUpdateChecks(registration, serviceWorkerUrl, {
          onChecked: () => { void announceAvailableUpdate() },
        })
        void announceAvailableUpdate()
        void lifecycle.initialCheck
      },
    })
    recordDiagnosticMilestone('service-worker:registration-started')
  })
}

const nativePlatform = getNativePlatform(window)
if (nativePlatform) {
  recordDiagnosticMilestone('service-worker:disabled-native', { nativePlatform })
  void navigator.serviceWorker?.getRegistrations().then((registrations) =>
    removeNativeAppShellWorkers(registrations, globalThis.caches),
  )
} else {
  window.addEventListener('minimany:interactive', startServiceWorker, { once: true })
  globalThis.setTimeout(startServiceWorker, 3000)
}
